// src/prototype/components/oir/report/OIRReportIndex.tsx
// ============================================================
// Sticky left rail navigation for OIRReportView
// - Smooth scroll to section anchors
// - IntersectionObserver active section highlight
// - Mobile: collapses to "Jump to section" dropdown
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

const APP_HEADER_HEIGHT_PX = 72;
const STICKY_HEADER_BUFFER_PX = 12;
const EA_STICKY_HEADER_ID = 'ea-opportunities-sticky-header';
const STICKY_OFFSET_CSS_VAR = '--oir-sticky-offset';

// ── Section definitions ────────────────────────────────────────────────────
// Order updated March 2026: Financial Impact moved above Strategy Decision Canvas
// to support executive cognition: value first, then selection.
export const REPORT_SECTIONS = [
  { id: 'oir-exec-summary',              label: 'Executive Summary' },
  { id: 'oir-strategic-context',         label: 'Strategic Context' },
  { id: 'oir-opportunity-landscape',     label: 'Opportunity Landscape' },
  { id: 'oir-financial-impact',          label: 'Financial Impact' },
  { id: 'oir-strategy-decision-canvas',  label: 'Strategy Decision Canvas' },
  { id: 'oir-execution-roadmap',         label: 'Execution Roadmap' },
] as const;

type SectionId = typeof REPORT_SECTIONS[number]['id'];

function measureStickyTopPx(): number {
  const eaHeader = document.getElementById(EA_STICKY_HEADER_ID);
  const eaHeaderHeight = eaHeader?.getBoundingClientRect().height ?? 0;
  return APP_HEADER_HEIGHT_PX + eaHeaderHeight + STICKY_HEADER_BUFFER_PX;
}

function applyStickyOffsetCssVar(offsetPx: number) {
  document.documentElement.style.setProperty(STICKY_OFFSET_CSS_VAR, `${offsetPx}px`);
}

// ── Component ──────────────────────────────────────────────────────────────
export const OIRReportIndex: React.FC = () => {
  const [activeId, setActiveId] = useState<SectionId>(REPORT_SECTIONS[0].id);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [stickyTopPx, setStickyTopPx] = useState(
    APP_HEADER_HEIGHT_PX + 56 + STICKY_HEADER_BUFFER_PX,
  );
  const syncStickyOffset = useCallback(() => {
    const next = measureStickyTopPx();
    setStickyTopPx(next);
    applyStickyOffsetCssVar(next);
  }, []);

  useEffect(() => {
    syncStickyOffset();

    const eaHeader = document.getElementById(EA_STICKY_HEADER_ID);
    const resizeObserver = eaHeader ? new ResizeObserver(syncStickyOffset) : null;
    resizeObserver?.observe(eaHeader!);

    window.addEventListener('resize', syncStickyOffset);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncStickyOffset);
    };
  }, [syncStickyOffset]);

  const resolveActiveSection = useCallback((): SectionId => {
    const offset = measureStickyTopPx();
    const scrollPosition = window.scrollY + offset + 24;

    const nearBottom =
      window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 80;

    if (nearBottom) {
      return REPORT_SECTIONS[REPORT_SECTIONS.length - 1].id;
    }

    let current = REPORT_SECTIONS[0].id;
    for (const section of REPORT_SECTIONS) {
      const el = document.getElementById(section.id);
      if (!el) continue;
      if (el.offsetTop <= scrollPosition) {
        current = section.id;
      }
    }
    return current;
  }, []);

  const scrollToSection = useCallback((id: SectionId) => {
    setActiveId(id);

    const el = document.getElementById(id);
    if (!el) return;
    const offset = measureStickyTopPx();
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    let rafId = 0;

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setActiveId(resolveActiveSection());
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [resolveActiveSection, stickyTopPx]);

  const activeLabel = REPORT_SECTIONS.find(s => s.id === activeId)?.label ?? '';

  const navStyle: React.CSSProperties = {
    top: stickyTopPx,
    maxHeight: `calc(100vh - ${stickyTopPx}px)`,
  };

  return (
    <>
      {/* ── Desktop: sticky left rail (aside stretches to content height for sticky) ── */}
      <aside className="hidden md:flex w-[240px] flex-shrink-0 self-stretch">
        <nav
          className="sticky z-[5] flex w-full flex-col overflow-y-auto overscroll-contain py-1"
          style={navStyle}
          aria-label="Report section index"
        >
          {/* Index header */}
          <p className="text-[10px] font-semibold text-[#18365E]/40 uppercase tracking-widest mb-3 px-1">
            Report Index
          </p>

          <ol className="flex flex-col gap-0.5">
            {REPORT_SECTIONS.map((section, idx) => {
              const isActive = activeId === section.id;
              return (
                <li key={section.id}>
                  <button
                    onClick={() => scrollToSection(section.id as SectionId)}
                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition-all group ${
                      isActive
                        ? 'bg-[#18365E]/[0.07] text-[#18365E] font-semibold'
                        : 'text-[#717182] hover:text-[#18365E] hover:bg-[#18365E]/[0.04] font-medium'
                    }`}
                  >
                    {/* Step number */}
                    <span
                      className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                        isActive
                          ? 'bg-[#18365E] text-white'
                          : 'bg-[#18365E]/10 text-[#18365E]/50 group-hover:bg-[#18365E]/20 group-hover:text-[#18365E]/70'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    {section.label}
                  </button>
                </li>
              );
            })}
          </ol>

          {/* Divider + branding */}
          <div className="mt-5 pt-4 border-t border-[#e5e7eb]">
            <p className="text-[9px] text-[#717182]/60 px-1 leading-relaxed">
              Opportunity Intelligence Report
              <br />
              <span className="text-[#FF6E4C]/70">DVA · Adaptive Product</span>
            </p>
          </div>
        </nav>
      </aside>

      {/* ── Mobile: dropdown ───────────────────────────────────────────── */}
      <div className="md:hidden mb-3">
        <button
          onClick={() => setMobileOpen(v => !v)}
          className="flex items-center justify-between w-full px-3 py-2 bg-white border border-[#e5e7eb] rounded-lg text-xs font-semibold text-[#18365E] shadow-sm"
        >
          <span>Jump to: {activeLabel}</span>
          <ChevronDown
            className={`w-3.5 h-3.5 text-[#18365E]/50 transition-transform ${mobileOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {mobileOpen && (
          <div className="mt-1 bg-white border border-[#e5e7eb] rounded-lg shadow-md overflow-hidden z-30">
            {REPORT_SECTIONS.map((section, idx) => (
              <button
                key={section.id}
                onClick={() => {
                  scrollToSection(section.id as SectionId);
                  setMobileOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 text-xs text-[#18365E] hover:bg-[#18365E]/[0.05] flex items-center gap-2 border-b border-[#e5e7eb] last:border-0"
              >
                <span className="w-4 h-4 rounded-full bg-[#18365E]/10 text-[#18365E]/60 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                  {idx + 1}
                </span>
                {section.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default OIRReportIndex;
